import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Invoice, InvoiceService, PixResponse, PaymentStatusResponse, PendingPaymentResponse } from '../services/invoice.service';
import { UserStorageService } from '../services/user-storage.service';
import { PaymentDataModalComponent } from './payment-data-modal.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-invoice-detail-modal',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50"
         *transloco="let t"
         (click)="onClose()">
      <div class="bg-white shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" 
           [ngClass]="{'ring-4 ring-red-300': invoice.isOverdue}"
           style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);"
           (click)="$event.stopPropagation()">
        <!-- Alerta de Vencida -->
        <div *ngIf="invoice.isOverdue" class="bg-red-600 text-white px-6 py-3 flex items-center gap-3 animate-pulse">
          <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <div class="flex-1">
            <p class="font-bold text-lg">{{ t('invoices.detail.overdueTitle') }}</p>
            <p class="text-sm text-red-100">{{ t('invoices.detail.overdueText') }}</p>
          </div>
        </div>
        
        <!-- Header -->
        <div class="px-6 py-4 border-b" [ngClass]="invoice.isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-100 border-gray-200'">
          <p class="text-sm text-gray-400 mb-1">{{ t('invoices.detail.details') }}</p>
          <p class="text-base font-semibold" [ngClass]="invoice.isOverdue ? 'text-red-800' : 'text-gray-800'">{{ t('invoices.detail.number') }} {{ invoice.invoiceNumber }}</p>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- QR Code View -->
          <div *ngIf="showingQrCode && pixResponse" class="text-center">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">
              <span *ngIf="!isPaymentConfirmed">{{ t('invoices.detail.qrTitle') }}</span>
              <span *ngIf="isPaymentConfirmed" class="text-green-600">{{ t('invoices.detail.paymentConfirmed') }}</span>
            </h3>
            <div class="flex justify-center mb-4">
              <!-- QR Code -->
              <div *ngIf="!isPaymentConfirmed">
                <img 
                  [src]="'data:image/png;base64,' + pixResponse.qrCodeBase64" 
                  alt="QR Code PIX"
                  class="max-w-xs border border-gray-300">
              </div>
              <!-- Check de Confirmação -->
              <div *ngIf="isPaymentConfirmed" class="flex flex-col items-center">
                <div class="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-20 h-20 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <p class="text-green-600 font-semibold text-lg">{{ t('invoices.detail.paymentConfirmedSuccess') }}</p>
              </div>
            </div>
            <div class="mt-4" *ngIf="!isPaymentConfirmed">
              <a 
                [href]="pixResponse.ticketUrl" 
                target="_blank"
                class="text-blue-600 hover:text-blue-800 underline text-sm">
                {{ t('invoices.detail.moreDetails') }}
              </a>
            </div>
          </div>

          <!-- Normal Invoice Details View -->
          <div *ngIf="!showingQrCode">
            <!-- Status e Informações Principais -->
            <div class="mb-6">
            <div class="flex justify-end items-start mb-4">
              <span [ngClass]="getStatusClass(invoice.status)" class="px-4 py-2 rounded-full text-sm font-medium">
                {{ getStatusText(invoice.status) }}
              </span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p class="text-xs text-gray-500 mb-1">{{ t('invoices.detail.createdDate') }}</p>
                <p class="text-sm font-medium">{{ invoice.createdDate | date:'dd/MM/yyyy HH:mm' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500 mb-1">{{ t('invoices.detail.dueDate') }}</p>
                <p class="text-sm font-medium">{{ invoice.dueDate | date:'dd/MM/yyyy' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500 mb-1">{{ t('invoices.detail.lastUpdate') }}</p>
                <p class="text-sm font-medium">{{ invoice.updatedDate | date:'dd/MM/yyyy HH:mm' }}</p>
              </div>
            </div>

            <div class="p-4" [ngClass]="invoice.isOverdue ? 'bg-red-50 border-2 border-red-300' : 'bg-green-50 border border-green-200'">
              <p class="text-xs mb-1" [ngClass]="invoice.isOverdue ? 'text-red-700 font-semibold' : 'text-gray-500'">{{ t('invoices.totalAmount') }}</p>
              <p class="text-2xl font-bold" [ngClass]="invoice.isOverdue ? 'text-red-600' : 'text-green-600'">{{ formatCurrency(invoice.totalAmount) }}</p>
            </div>
          </div>

          <!-- Itens da Fatura -->
          <div *ngIf="invoice.items && invoice.items.length > 0">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">{{ t('invoices.detail.invoiceItems') }}</h3>
            <div class="border border-gray-200 overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('invoices.detail.colDescription') }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('invoices.detail.colValue') }}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr *ngFor="let item of invoice.items" class="hover:bg-gray-50">
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ item.id }}</td>
                    <td class="px-4 py-3 text-sm text-gray-900">{{ item.description }}</td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                      {{ formatCurrency(item.value) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-50">
                  <tr>
                    <td colspan="2" class="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{{ t('invoices.detail.totalLabel') }}</td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-right text-green-600">
                      {{ formatCurrency(invoice.totalAmount) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div *ngIf="!invoice.items || invoice.items.length === 0" class="text-center py-8 text-gray-500">
            <p>{{ t('invoices.detail.noItems') }}</p>
          </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button 
            *ngIf="showingQrCode"
            (click)="backToDetails()"
            class="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors">
            {{ t('invoices.detail.backToDetails') }}
          </button>
          <button
            *ngIf="canPay() && !showingQrCode"
            (click)="payNow()"
            class="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors">
            {{ t('invoices.payNow') }}
          </button>
          <button
            (click)="onClose()"
            class="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 transition-colors">
            {{ t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class InvoiceDetailModalComponent implements OnDestroy {
  invoice: Invoice;
  showingQrCode: boolean = false;
  pixResponse: PixResponse | null = null;
  private idempotencyKey: string;
  isPaymentConfirmed: boolean = false;
  private paymentStatusSubscription?: Subscription;
  private pollingInterval?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<InvoiceDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { invoice: Invoice; pixResponse?: PixResponse; showingQrCode?: boolean; idempotencyKey?: string },
    private invoiceService: InvoiceService,
    private userStorageService: UserStorageService,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private transloco: TranslocoService
  ) {
    this.invoice = data.invoice;
    // Usar idempotencyKey passado ou gerar um novo
    this.idempotencyKey = data.idempotencyKey || this.generateGuid();
    if (data.pixResponse && data.showingQrCode) {
      this.pixResponse = data.pixResponse;
      this.showingQrCode = true;
      this.startPaymentPolling();
    } else {
      // Se não há pixResponse já passado, verificar se existe um PIX pendente
      this.checkPendingPayment();
    }
  }

  ngOnDestroy(): void {
    this.stopPaymentPolling();
  }

  onClose(): void {
    // Se o pagamento já foi confirmado, apenas recarregar as faturas
    if (this.isPaymentConfirmed) {
      this.dialogRef.close({ shouldReload: true });
      return;
    }
    
    // Se estiver mostrando QR code mas não foi confirmado, verificar status antes de fechar
    if (this.showingQrCode) {
      this.checkPaymentStatusAndClose();
    } else {
      this.dialogRef.close();
    }
  }

  private checkPaymentStatusAndClose(): void {
    this.stopPaymentPolling();
    this.invoiceService.checkPaymentStatus(this.invoice.id)
      .subscribe({
        next: (status) => {
          // Se retornou isPaid = true, informar que precisa recarregar
          this.dialogRef.close({ shouldReload: status.isPaid });
        },
        error: (error) => {
          console.error('Erro ao verificar status do pagamento:', error);
          // Em caso de erro, apenas fechar a modal
          this.dialogRef.close({ shouldReload: false });
        }
      });
  }

  getStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      0: this.transloco.translate('invoices.status.pending'),
      1: this.transloco.translate('invoices.status.issued'),
      2: this.transloco.translate('invoices.status.paid'),
      3: this.transloco.translate('invoices.status.cancelled')
    };
    return statusMap[status] || this.transloco.translate('invoices.status.unknown');
  }

  getStatusClass(status: number): string {
    const classMap: { [key: number]: string } = {
      0: 'bg-yellow-100 text-yellow-800',
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-green-100 text-green-800',
      3: 'bg-gray-100 text-gray-800'
    };
    return classMap[status] || 'bg-gray-100 text-gray-800';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  canPay(): boolean {
    const status = Number(this.invoice.status);
    return status === 0 || status === 1;
  }

  payNow(): void {
    // Primeiro verificar se já existe um PIX pendente
    this.invoiceService.getPendingPayment(this.invoice.id)
      .subscribe({
        next: (response: PendingPaymentResponse) => {
          if (response.hasPendingPayment && response.pixResponse) {
            // Se existe PIX pendente, apenas mostrar o QR Code
            this.pixResponse = response.pixResponse;
            this.showingQrCode = true;
            this.startPaymentPolling();
          } else {
            // Se não existe, seguir o fluxo normal (abrir modal de dados e gerar novo PIX)
            const userData = this.userStorageService.getUserData();

            const dialogRef = this.dialog.open(PaymentDataModalComponent, {
              width: '500px',
              maxWidth: '90vw',
              data: userData ? {
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName
              } : {}
            });

            dialogRef.afterClosed().subscribe(result => {
              if (result) {
                this.userStorageService.saveUserData(result.email, result.firstName, result.lastName);
                this.generatePix(result.email, result.firstName, result.lastName);
              }
            });
          }
        },
        error: (error) => {
          console.error('Erro ao verificar pagamento pendente:', error);
          // Em caso de erro, seguir o fluxo normal
          const userData = this.userStorageService.getUserData();

          const dialogRef = this.dialog.open(PaymentDataModalComponent, {
            width: '500px',
            maxWidth: '90vw',
            data: userData ? {
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName
            } : {}
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              this.userStorageService.saveUserData(result.email, result.firstName, result.lastName);
              this.generatePix(result.email, result.firstName, result.lastName);
            }
          });
        }
      });
  }

  private generatePix(email: string, firstName: string, lastName: string): void {
    this.spinner.show();
    this.invoiceService.generatePix(this.invoice.id, email, firstName, lastName, this.idempotencyKey)
      .subscribe({
        next: (response) => {
          this.pixResponse = response;
          this.showingQrCode = true;
          this.spinner.hide();
          this.startPaymentPolling();
        },
        error: (error) => {
          console.error('Erro ao gerar PIX:', error);
          this.spinner.hide();
          this.toastr.error(this.transloco.translate('invoices.toast.qrError'), this.transloco.translate('toasts.error'));
        }
      });
  }

  private startPaymentPolling(): void {
    // Parar qualquer polling anterior
    this.stopPaymentPolling();
    
    // Iniciar polling a cada 5 segundos
    this.pollingInterval = interval(5000).subscribe(() => {
      if (this.showingQrCode && !this.isPaymentConfirmed) {
        this.checkPaymentStatus();
      }
    });
    
    // Fazer primeira verificação imediatamente
    this.checkPaymentStatus();
  }

  private stopPaymentPolling(): void {
    if (this.pollingInterval) {
      this.pollingInterval.unsubscribe();
      this.pollingInterval = undefined;
    }
    if (this.paymentStatusSubscription) {
      this.paymentStatusSubscription.unsubscribe();
      this.paymentStatusSubscription = undefined;
    }
  }

  private checkPaymentStatus(): void {
    if (this.isPaymentConfirmed) {
      return;
    }

    this.paymentStatusSubscription = this.invoiceService.checkPaymentStatus(this.invoice.id)
      .subscribe({
        next: (status: PaymentStatusResponse) => {
          if (status.isPaid) {
            this.isPaymentConfirmed = true;
            this.stopPaymentPolling();
          }
        },
        error: (error) => {
          console.error('Erro ao verificar status do pagamento:', error);
          // Não parar o polling em caso de erro, continuar tentando
        }
      });
  }

  backToDetails(): void {
    this.stopPaymentPolling();
    this.showingQrCode = false;
    this.pixResponse = null;
    this.isPaymentConfirmed = false;
  }

  private checkPendingPayment(): void {
    this.invoiceService.getPendingPayment(this.invoice.id)
      .subscribe({
        next: (response: PendingPaymentResponse) => {
          if (response.hasPendingPayment && response.pixResponse) {
            this.pixResponse = response.pixResponse;
            this.showingQrCode = true;
            this.startPaymentPolling();
          }
        },
        error: (error) => {
          console.error('Erro ao verificar pagamento pendente:', error);
          // Em caso de erro, apenas não fazer nada (mostrar detalhes normais)
        }
      });
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

